import { deepEqual, formatNumberToCurrency } from 'src/Elements/Utils/general'

// NOTE: move these a generic utility file instead of Checkout/V2
import { SummaryLineItem, SummaryData } from 'src/Elements/Checkout/V2/types'
import { CartData } from 'yggdrasil-blueprints/src/Elements/Cart/types'
import { isResponseError, FetcherError } from 'javascript/lander/utils/fetcher'
import { hasPhysicalProducts } from '../Services/checkout-utils'
import { hasErrors, productErrors } from '../RebillyCheckout/checkout-globals'
import { makeCurrentPageFetcher } from '../Utils/api'

// TODO: implement types
type Address = any
type Cart = any

export type OrderPreviewPayload = {
  address?: Address
  shippingOption?: ShippingOption
  cart: Cart
  integrations_stripe_installation_id?: string
  coupon_code?: string
}

export type ServerAddressAttributes =
  | {
      id: number
    }
  | {
      address_one: string
      address_two: string
      city: string
      state: string
      country: number
      postal_code: string
    }

type ServerReqSummaryAddresses = {
  billing_address?: ServerAddressAttributes
  delivery_address?: ServerAddressAttributes
}

type ServerReqSummaryLineItem = {
  line_item_id?: number
  variant_id: number
  price_id: number
  quantity: number
}

type ServerReqSummaryCart = {
  order_id: number
  line_items: ServerReqSummaryLineItem[]
}
type ServerReqSummaryShippingOption = {
  selected_shipping_option?: ShippingOption
}

type ServerReqSummary = {
  order: { full_preview: boolean; coupon_codes?: string } & ServerReqSummaryAddresses &
    ServerReqSummaryCart &
    ServerReqSummaryShippingOption
  integrations_stripe_installation_id?: string
}

export type ShippingOption = {
  description: string
  amount: { amount: string; currency: string }
  is_custom: boolean
  amount_formatted: string
  amount_cents: number
}

export type ServerRespSummaryData = {
  currency: string
  currency_symbol: string
  line_items: SummaryLineItem[]
  billing_address: { city: string; region: string }
  shipping_quotes_response?: { options?: ShippingOption[] }
  selected_shipping_option: any
  total_amount: {
    amount: string
  }
  total_amount_formatted: string
  total_amount_cents: number
  line_item_subtotal: {
    amount: string
  }
  line_item_subtotal_formatted: string
  line_item_subtotal_cents: number
  discount_amount: {
    amount: string
  }
  discount_amount_formatted: string
  discount_amount_cents: number
  discounts: { name: string; code: string; amount: number }[]
  subtotal_amount: {
    amount: string
  }
  next_invoice_date?: string
  subtotal_amount_formatted: string
  subtotal_amount_cents: number
  shipping_amount: {
    amount: string
  }
  shipping_amount_formatted: string
  shipping_amount_cents: number
  tax_amount: {
    amount: string
  }
  tax_amount_formatted: string
  tax_amount_cents: number
  upcoming_invoice?: {
    interim_amount: {
      amount: string
    }
    interim_amount_formatted: string
    interim_amount_cents: number
    line_items: {
      variant_name: string
      quantity: number
      amount: {
        amount: string
      }
      interim: boolean
      amount_formatted: string
    }[]
  }
}

const orderPreviewFetcher = makeCurrentPageFetcher<ServerRespSummaryData>({
  customEvent: 'CF2:OrderPreview',
})

let lastOrderPreviewData: ServerReqSummary
let lastOrderPreviewResponse: ServerRespSummaryData

export function hasValidDataForLineItemSubtotal(payload: OrderPreviewPayload): boolean {
  const { cart } = payload

  const productSelectErrors = productErrors(cart, true)
  return !hasErrors(productSelectErrors)
}

export async function fetchOrderPreview(
  payload: OrderPreviewPayload,
  onStart?: () => void
): Promise<ServerRespSummaryData | FetcherError> {
  const dataToPreview = buildOrderPreviewPayload(payload)
  if (lastOrderPreviewResponse && deepEqual(dataToPreview, lastOrderPreviewData)) {
    // Supersede the preview in flight as a sent request would, or its late response overwrites this one.
    orderPreviewFetcher.abort()
    return lastOrderPreviewResponse
  }
  if (onStart) onStart()
  const response = await orderPreviewFetcher.post('/order_preview', dataToPreview)
  if (!isResponseError(response)) {
    // Cached together, so a payload is only ever answered with its own response.
    lastOrderPreviewData = dataToPreview
    lastOrderPreviewResponse = response
    return lastOrderPreviewResponse
  } else {
    return response
  }
}

export function processOrderPreviewResponseForSummary(summaryData: ServerRespSummaryData): SummaryData {
  let nextInvoiceText: string
  if (summaryData.next_invoice_date) {
    const nextInvoiceDate = new Date(summaryData.next_invoice_date)
    const currentDate = new Date()

    const diff = nextInvoiceDate.getTime() - currentDate.getTime()
    // NOTE: if invoice date is greater than 5 minutes, display due date
    if (diff > 5 * 60 * 1000) {
      // NOTE: If the payment will be done within 24 hours display the time when the payment will occur
      if (diff < 24 * 60 * 60 * 1000) {
        nextInvoiceText = nextInvoiceDate.toLocaleString()
      } else {
        nextInvoiceText = nextInvoiceDate.toLocaleDateString()
      }
    }
  }
  return {
    currency: summaryData.currency,
    line_items: summaryData.line_items,
    total: {
      amount: parseFloat(summaryData.total_amount?.amount),
      formatted: summaryData.total_amount_formatted,
      amount_cents: summaryData.total_amount_cents,
    },
    discount: {
      amount: parseFloat(summaryData.discount_amount?.amount),
      formatted: summaryData.discount_amount_formatted,
      amount_cents: summaryData.discount_amount_cents,
    },
    lineItemSubtotal: {
      amount: parseFloat(summaryData.line_item_subtotal?.amount),
      formatted: summaryData.line_item_subtotal_formatted,
      amount_cents: summaryData.line_item_subtotal_cents,
    },
    discounts: (summaryData.discounts ?? []).map((discount) => {
      return {
        name: discount.name,
        code: discount.code,
        amount_formatted: '-' + formatNumberToCurrency(summaryData.currency_symbol, discount.amount),
      }
    }),
    ...(nextInvoiceText ? { next_invoice_date: nextInvoiceText } : {}),
    subtotal: {
      amount: parseFloat(summaryData.subtotal_amount.amount),
      formatted: summaryData.subtotal_amount_formatted,
      amount_cents: summaryData.subtotal_amount_cents,
    },
    shipping: {
      amount: parseFloat(summaryData.shipping_amount.amount),
      formatted: summaryData.shipping_amount_formatted,
      amount_cents: summaryData.shipping_amount_cents,
    },
    tax: {
      amount: parseFloat(summaryData.tax_amount.amount),
      formatted: summaryData.tax_amount_formatted,
      amount_cents: summaryData.tax_amount_cents,
    },
    ...(summaryData.upcoming_invoice?.interim_amount
      ? {
          interim: {
            amount: parseFloat(summaryData.upcoming_invoice.interim_amount.amount),
            formatted: summaryData.upcoming_invoice?.interim_amount_formatted,
            amount_cents: summaryData.upcoming_invoice?.interim_amount_cents,
          },
        }
      : {}),
    ...(summaryData.upcoming_invoice
      ? {
          upcoming_invoice: {
            line_items: summaryData.upcoming_invoice.line_items
              .filter((i) => i.interim)
              .map((item) => {
                return {
                  variant_name: item.variant_name,
                  quantity: item.quantity,
                  amount: parseFloat(item.amount.amount),
                  formatted: item.amount_formatted,
                }
              }),
          },
        }
      : {}),
  }
}

function buildCartDetails(cart: CartData): ServerReqSummaryCart {
  const orderId = cart.order_id
  const lineItems = cart.items.map(({ price_id, line_item_id, variant_id, quantity }) => {
    const data: ServerReqSummaryLineItem = {
      variant_id: variant_id,
      price_id: price_id,
      ...(line_item_id ? { line_item_id: line_item_id } : {}),
      quantity,
    }
    return data
  })
  return { order_id: orderId, line_items: lineItems }
}

export function buildOrderPreviewPayload(payload: OrderPreviewPayload): ServerReqSummary {
  const { address, shippingOption, cart, integrations_stripe_installation_id, coupon_code } = payload
  const isPhysical = hasPhysicalProducts(cart)
  return {
    integrations_stripe_installation_id,
    order: {
      full_preview: true,
      coupon_codes: coupon_code,
      ...(address ? buildAddressParams(address, isPhysical) ?? {} : {}),
      ...buildCartDetails(cart),
      ...(shippingOption?.amount ? { selected_shipping_option: shippingOption } : {}),
    },
  }
}

function buildAddressParams(address: Address, isPhysical: boolean): ServerReqSummaryAddresses {
  // NOTE: Maybe we dont need the id verification here as stripe will tackle saved addresses for us

  const delivery_address = { ...address }
  const billing_address = address.id ? { id: address.id } : { ...address }

  if (isPhysical) {
    return {
      delivery_address,
      billing_address,
    }
  } else {
    return { billing_address }
  }
}
